/**
 * Pure helper that wraps the story-map creation flow with consistent
 * user-feedback handling. Extracted so the toast.error path on insert
 * failure is unit-testable (audit H6).
 */

export interface CreateStoryMapInput {
    name: string;
    description: string;
    userId: string;
}

export interface CreateStoryMapDeps {
    create: (data: { owner_id: string; name: string; description?: string }) =>
        Promise<{ data: { id: string } | null; error: { message?: string } | null }>;
    toastError: (msg: string) => void;
    toastSuccess: (msg: string) => void;
    navigate: (path: string) => void;
    onSuccess?: () => void;
}

/**
 * Returns true on success (insert ok + side-effects triggered), false otherwise.
 */
export async function createStoryMap(
    input: CreateStoryMapInput,
    deps: CreateStoryMapDeps,
): Promise<boolean> {
    const name = input.name.trim();
    if (!name) {
        return false;
    }

    try {
        const { data, error } = await deps.create({
            owner_id: input.userId,
            name,
            description: input.description,
        });

        if (error || !data) {
            const detail = error?.message ?? 'unknown error';
            deps.toastError(`Failed to create story map: ${detail}`);
            return false;
        }

        deps.toastSuccess('Story map created');
        deps.navigate(`/story-maps/${data.id}`);
        deps.onSuccess?.();
        return true;
    } catch (err) {
        const detail = err instanceof Error ? err.message : 'unknown error';
        deps.toastError(`Failed to create story map: ${detail}`);
        return false;
    }
}
